import { apiTransport } from './api-client';
import type { Page } from './collections';

/**
 * Numbered pages over a cursor-paged endpoint.
 *
 * The screens page by number; the API pages by cursor. The queues sent
 * neither — every "page 2" re-read page 1 — so a long leave or corrections
 * queue showed its first twenty-five rows under every page number. The cursor
 * each page ended on is remembered per query, and a page is asked for with
 * the cursor of the page before it. Jumping further than the pages already
 * seen lands on the furthest page known, rather than inventing a cursor.
 */
const cursors = new Map<string, string[]>();

export async function cursorPage<TApi, TRow>(
  path: string,
  query: Record<string, string | number | boolean | undefined>,
  paging: { page?: number; pageSize?: number },
  map: (row: TApi) => TRow,
): Promise<Page<TRow>> {
  const pageSize = paging.pageSize ?? 25;
  const pageNumber = paging.page ?? 1;
  const key = JSON.stringify([path, query, pageSize]);
  const known = cursors.get(key) ?? [];
  const cursor = pageNumber <= 1 ? undefined : known[Math.min(pageNumber - 2, known.length - 1)];

  const page = await apiTransport().page<TApi>(path, {
    query: { ...query, limit: pageSize, cursor },
  });

  if (page.pageInfo.nextCursor) {
    const next = [...known];
    next[pageNumber - 1] = page.pageInfo.nextCursor;
    cursors.set(key, next);
  }

  return { rows: page.items.map(map), total: page.totalCount, page: pageNumber, pageSize };
}
